/**
 * Shared helpers for the Strategy step's annuity purchase section.
 *
 * Kept out of the components so the desktop form, the mobile form and the
 * review summary all derive defaults, captions and the indicative rate the
 * same way.
 */

import type { AnnuityEscalation, AnnuityPurchaseData, PersonData } from '../../types/wizard';
import { useCallback, useState } from 'react';
import { getMinimumPensionAge, parseLocalDate } from '../../types/wizard';
import {
  ANNUITY_RATES_AS_OF_LABEL, ANNUITY_RATE_MAX_AGE, ANNUITY_RATE_MIN_AGE, indicativeAnnuityRate,
} from '../../constants/annuityRates';

/** Earliest age the wizard offers for anyone (the pre-April-1971 floor). */
export const ANNUITY_MIN_PURCHASE_AGE = 55;

/**
 * UK minimum pension age for this person: 55 or 57 by date of birth.
 *
 * The server rejects a purchase below it, so the wizard must use the same rule
 * rather than a flat 55 — otherwise someone born on or after 6 April 1971 can
 * enter 55 or 56, pass the wizard, and only fail at projection time.
 */
export function minimumAnnuityPurchaseAge(
  person: Pick<PersonData, 'dateOfBirth'> | undefined,
): number {
  if (!person?.dateOfBirth) return ANNUITY_MIN_PURCHASE_AGE;
  try {
    return getMinimumPensionAge(person.dateOfBirth);
  } catch {
    // An unparseable date is the people step's problem to report, not this
    // field's; fall back to the permissive floor rather than blocking entry.
    return ANNUITY_MIN_PURCHASE_AGE;
  }
}

/** Age the wizard suggests when nothing better can be derived. */
export const ANNUITY_DEFAULT_PURCHASE_AGE = 65;

/** Default share of the pension used, as a percentage. */
export const ANNUITY_DEFAULT_PCT = 25;

/** Default survivor share for a joint-life annuity. */
export const ANNUITY_DEFAULT_SURVIVOR_PCT = 50;

/** Default escalation for the "rising 3% a year" option. */
export const ANNUITY_DEFAULT_ESCALATION_PCT = 3;

export const ANNUITY_ESCALATION_LABELS: Record<AnnuityEscalation, string> = {
  LEVEL: 'Level',
  FIXED: 'Rising 3% a year',
  RPI: 'Inflation-linked',
};

/** The plan's inflation rate stands in for RPI — say so where the option is chosen. */
export const ANNUITY_RPI_WEDGE_NOTE =
  "Uses the plan's inflation rate; RPI has run about half to one point higher, so this understates the income.";

/**
 * A fixed-rate lifetime annuity is not a Money Purchase Annual Allowance
 * trigger, and the tool models no contributions after purchase.
 */
export const ANNUITY_MPAA_NOTE =
  'Buying a fixed-rate lifetime annuity does not trigger the Money Purchase Annual Allowance, so it does not restrict future pension contributions. This plan models no contributions after purchase in any case.';

/** Whole years between two dates. */
function wholeYearsBetween(from: Date, to: Date): number {
  let years = to.getFullYear() - from.getFullYear();
  const monthDiff = to.getMonth() - from.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && to.getDate() < from.getDate())) years--;
  return years;
}

/** A person's age at their planned retirement date, or null when either date is missing/invalid. */
export function retirementAge(person: Pick<PersonData, 'dateOfBirth' | 'retirementDate'> | undefined): number | null {
  if (!person?.dateOfBirth || !person?.retirementDate) return null;
  const dob = parseLocalDate(person.dateOfBirth);
  const retirement = parseLocalDate(person.retirementDate);
  if (!dob || !retirement) return null;
  const age = wholeYearsBetween(dob, retirement);
  return Number.isFinite(age) ? age : null;
}

/** Suggested purchase age: the later of 65 and the person's retirement age. */
export function defaultPurchaseAge(person: Pick<PersonData, 'dateOfBirth' | 'retirementDate'> | undefined): number {
  const retiring = retirementAge(person);
  if (retiring == null) return ANNUITY_DEFAULT_PURCHASE_AGE;
  return Math.max(ANNUITY_DEFAULT_PURCHASE_AGE, Math.max(ANNUITY_MIN_PURCHASE_AGE, retiring));
}

/** A fresh purchase row for a person, with the indicative rate already filled in. */
export function makeDefaultAnnuityPurchase(
  person: Pick<PersonData, 'dateOfBirth' | 'retirementDate'> | undefined,
): AnnuityPurchaseData {
  const purchaseAge = defaultPurchaseAge(person);
  return {
    purchaseId: `annuity-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    purchaseAge,
    amountMode: 'PERCENT',
    amountPct: ANNUITY_DEFAULT_PCT,
    takeTaxFreeCash: true,
    annualRatePct: indicativeAnnuityRate(purchaseAge, false, 'LEVEL'),
    escalation: 'LEVEL',
    jointLife: false,
  };
}

/**
 * Re-fill the indicative rate after an age, income-type or joint-life change,
 * unless the user has typed their own quote.
 */
export function withRefreshedRate(purchase: AnnuityPurchaseData): AnnuityPurchaseData {
  if (purchase.rateEdited || !Number.isFinite(purchase.purchaseAge)) return purchase;
  return {
    ...purchase,
    annualRatePct: indicativeAnnuityRate(purchase.purchaseAge, purchase.jointLife, purchase.escalation),
  };
}

/**
 * Caption under the rate field: what a healthy person of this age is quoted
 * today. Outside the table's 55-75 range the figure is the clamped one, so it
 * is labelled as the nearest quoted age rather than passed off as the user's.
 */
export function annuityRateCaption(purchase: AnnuityPurchaseData): string {
  const age = Number.isFinite(purchase.purchaseAge) ? purchase.purchaseAge : ANNUITY_DEFAULT_PURCHASE_AGE;
  const indicative = indicativeAnnuityRate(age, purchase.jointLife, purchase.escalation);
  const who = age > ANNUITY_RATE_MAX_AGE
    ? `the oldest age quoted, ${ANNUITY_RATE_MAX_AGE}`
    : `a healthy ${Math.max(age, ANNUITY_RATE_MIN_AGE)}-year-old`;
  const rangeNote = age < ANNUITY_RATE_MIN_AGE
    ? ` (rates are not quoted below ${ANNUITY_RATE_MIN_AGE})`
    : '';
  return `Annual income per £100 bought. Indicative for ${who}${rangeNote}, ${ANNUITY_RATES_AS_OF_LABEL}: ${indicative.toFixed(2)}. Enter your own quote.`;
}

/**
 * An annuity is a one-way decision, and the rate a quote offers moves with
 * gilt yields — which matters here because the purchase can be decades away.
 */
export const ANNUITY_IRREVERSIBLE_NOTE =
  'Buying an annuity cannot be reversed: the money leaves the pension permanently in exchange for the income. Quoted rates move with gilt yields, so a rate entered today may be materially different by the year of purchase — worth revisiting as that year approaches.';

/**
 * Tracks which annuity sections are holding an entry the step must not advance
 * past, so the desktop and mobile Strategy steps gate identically.
 *
 * Lifted into a helper because the round-4 guard was wired on desktop only:
 * on mobile the error showed, nothing was written to state, and Continue still
 * worked — so the step advanced with the draft holding the default. The box
 * read 55 while the projection ran 65, which is worse than the server error
 * the guard replaced.
 */
export function useAnnuityBlockedState(): {
  annuityBlocked: boolean;
  handleAnnuityValidity: (personIndex: number, blocked: boolean) => void;
} {
  const [blockedBy, setBlockedBy] = useState<Record<number, boolean>>({});
  const handleAnnuityValidity = useCallback((personIndex: number, blocked: boolean) => {
    setBlockedBy(prev => (prev[personIndex] === blocked ? prev : { ...prev, [personIndex]: blocked }));
  }, []);
  return { annuityBlocked: Object.values(blockedBy).some(Boolean), handleAnnuityValidity };
}

/**
 * Replace (or remove) the first purchase in a person's list, leaving any
 * others alone.
 *
 * The wizard only renders one purchase per person, but a plan created through
 * MCP can hold several. Writing a single-element array would delete the rest
 * on any edit to the section, with no warning.
 */
export function replaceFirstAnnuityPurchase(
  existing: AnnuityPurchaseData[] | undefined,
  purchase: AnnuityPurchaseData | null,
): AnnuityPurchaseData[] {
  const rest = (existing ?? []).slice(1);
  return purchase ? [purchase, ...rest] : rest;
}

/** One-line summary for the review step, e.g. "Annuity at 65: 50% of pension, level, joint 50%". */
export function summariseAnnuityPurchase(purchase: AnnuityPurchaseData): string {
  const amount = purchase.amountMode === 'PERCENT'
    ? `${purchase.amountPct ?? 0}% of pension`
    : `£${Math.round(purchase.amountFixed ?? 0).toLocaleString('en-GB')}`;
  const type = purchase.escalation === 'FIXED'
    ? `rising ${purchase.escalationPct ?? ANNUITY_DEFAULT_ESCALATION_PCT}% a year`
    : ANNUITY_ESCALATION_LABELS[purchase.escalation].toLowerCase();
  const life = purchase.jointLife
    ? `joint ${purchase.survivorPct ?? ANNUITY_DEFAULT_SURVIVOR_PCT}%`
    : 'single life';
  const cash = purchase.takeTaxFreeCash ? ', 25% tax-free cash' : '';
  return `Annuity at ${purchase.purchaseAge}: ${amount}, ${type}, ${life}, ${purchase.annualRatePct} per £100${cash}`;
}
